import { IFuelConsumptionWindow, IFuelMachine, IFuelMachineOperatorStat } from 'types/fuelConsumption.interface'

export const FUEL_BAND_PCT = 15

export const FUEL_TELEMETRY_SILENCE_LIMIT_H = 6

export const FUEL_LEAGUE_SUBLINE = '30 days · all rates carry their source'

export const FUEL_LEAGUE_FOOTNOTE =
  'Rates show provenance — measured telemetry, between-fills SMU windows, or attributed share.'

export const FUEL_OPERATOR_FAIRNESS_CAPTION =
  'Differences are coaching signals, not discipline evidence — rates are attributed by operating time, not measured per operator.'

export const FUEL_TASK_FOOTNOTE = 'Feeds plan fuel cost in Operations → Control Room.'

export const isTelemetryDegraded = (machine: IFuelMachine): boolean =>
  machine.fuelDataMode === 'TELEMETRY' &&
  machine.telemetrySilentHours != null &&
  machine.telemetrySilentHours > FUEL_TELEMETRY_SILENCE_LIMIT_H

export const buildConsumptionSeries = (machine: IFuelMachine): IFuelConsumptionWindow[] => {
  if (machine.fuelDataMode === 'TELEMETRY' && !isTelemetryDegraded(machine)) {
    return machine.measuredSegments
  }
  return machine.fillWindows
}

export const avgProvenanceOf = (machine: IFuelMachine): IFuelConsumptionWindow['provenance'] =>
  machine.fuelDataMode === 'TELEMETRY' && !isTelemetryDegraded(machine) ? 'MEASURED' : 'BETWEEN_FILLS'

export const bandDeltaPct = (rate: number, nominal: number): number => {
  if (!nominal) return 0
  return Math.round(((rate - nominal) / nominal) * 1000) / 10
}

export const bandStateOf = (rate: number, nominal: number): 'over' | 'in' | 'under' => {
  const delta = bandDeltaPct(rate, nominal)
  if (delta > FUEL_BAND_PCT) return 'over'
  if (delta < -FUEL_BAND_PCT) return 'under'
  return 'in'
}

export const bandLabelOf = (rate: number, nominal: number): string => {
  const state = bandStateOf(rate, nominal)
  if (state === 'over') return 'over band'
  if (state === 'under') return 'under band'
  return 'in band'
}

export const signedPctLabel = (delta: number): string => `${delta > 0 ? '+' : ''}${delta}%`

export const machineHeadlineRateLph = (machine: IFuelMachine): number => {
  if (machine.flags.some((f) => f.kind === 'OVER_BAND')) {
    const usable = buildConsumptionSeries(machine)
      .map((w) => w.ratePerHour)
      .filter((r): r is number => r != null)
    if (usable.length) return Math.max(...usable)
  }
  return machine.avgRateLph
}

export const worstFuelMachines = (machines: IFuelMachine[]): IFuelMachine[] => {
  const score = (machine: IFuelMachine): number => {
    const flagScore = machine.flags.reduce(
      (sum, f) => sum + (f.tone === 'danger' ? 200 : 100),
      0,
    )
    return flagScore + bandDeltaPct(machine.avgRateLph, machine.nominalBurnRateLph)
  }
  return [...machines].sort((a, b) => score(b) - score(a)).slice(0, 5)
}

export const litresCostGhs = (litres: number, wacPerLitre: number): number =>
  Math.round(litres * wacPerLitre)

export const operatorFairnessGuard = (stat: IFuelMachineOperatorStat): boolean =>
  stat.windows < 3 || stat.hours < 20

export const machineClassText = (machine: IFuelMachine): string =>
  machine.machineClass === 'OPERATED_MOBILE' ? '⛟ Mobile' : '⚙ Static plant'
